import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const SyllableSorterGame = ({ gameData, onComplete }) => {
    const [currentWordIndex, setCurrentWordIndex] = useState(0);
    const [feedback, setFeedback] = useState(null);
    const [score, setScore] = useState(0);

    const currentWord = gameData?.words?.[currentWordIndex];

    const handleCategoryClick = (syllableCount) => {
        if (feedback || !currentWord) return;
        triggerHaptic(30);

        const isCorrect = currentWord.syllables === syllableCount;

        if (isCorrect) {
            setFeedback('correct');
            const points = gameData.points_per_word || 15;
            setScore(prev => prev + points);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                if (currentWordIndex + 1 >= gameData.words.length) {
                    onComplete?.(score + points);
                } else {
                    setCurrentWordIndex(prev => prev + 1);
                    setFeedback(null);
                }
            }, 3000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => setFeedback(null), 2000);
        }
    };

    if (!gameData || !currentWord) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center bg-white animate-pulse">
                <div className="text-9xl mb-8">⚛️</div>
                <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tighter uppercase">Inicijuojamas Syllable Collider...</h3>
            </div>
        );
    }

    const progress = (currentWordIndex / gameData.words.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[3rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-slate-950 text-white">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(139,92,246,0.15)_0%,transparent_70%)] pointer-events-none animate-pulse"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:p-6">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-violet-500/20 rounded-[1.5rem] md:rounded-[2rem] flex items-center justify-center text-xl md:text-5xl shadow-inner border border-white/10 animate-spin-slow">
                        ⚛️
                    </div>
                    <div>
                        <h3 className="text-base md:text-4xl font-black tracking-tighter leading-none uppercase bg-clip-text text-transparent bg-gradient-to-r from-violet-300 via-fuchsia-200 to-white">
                            Syllable Collider
                        </h3>
                        <p className="text-violet-300/60 font-black text-[10px] md:text-sm uppercase tracking-[0.4em] mt-1">
                            Žodžio struktūros analizatorius
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/5 backdrop-blur-xl px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border border-white/10">
                        <p className="text-[8px] md:text-[10px] font-black text-violet-400 uppercase tracking-[0.3em] mb-1">Energija</p>
                        <p className="text-sm md:text-3xl font-black text-white">⭐ {score}</p>
                    </div>
                    <div className="bg-white/5 backdrop-blur-xl px-4 py-2 md:px-8 md:py-4 rounded-xl md:rounded-[2rem] border border-white/10 min-w-[80px] md:min-w-[120px] text-center">
                        <p className="text-[8px] md:text-[10px] font-black text-violet-400 uppercase tracking-[0.3em] mb-1">Elementas</p>
                        <p className="text-sm md:text-3xl font-black text-white">{currentWordIndex + 1}/{gameData.words.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-8 w-full bg-white/5 rounded-full h-2 md:h-4 p-0.5 md:p-1 border border-white/10 overflow-hidden">
                <div 
                    className="h-full bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(139,92,246,0.5)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="premium-glass rounded-3xl md:rounded-[5rem] p-4 md:p-12 text-center border-white/40 shadow-2xl bg-white relative overflow-hidden flex flex-col items-center justify-center min-h-[300px]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(167,139,250,0.05)_0%,transparent_70%)] pointer-events-none"></div>
            
            <div className="relative group mb-6 md:mb-12">
               <div className="absolute -inset-10 md:inset-20 bg-violet-200 rounded-full blur-[100px] opacity-30 animate-pulse group-hover:opacity-50 transition-opacity"></div>
               <div className="text-[120px] md:text-[200px] drop-shadow-[0_20px_50px_rgba(0,0,0,0.1)] animate-float relative z-10">{currentWord.emoji}</div>
            </div>

            <div className="space-y-4 md:space-y-6 relative z-10">
                <h2 className="text-4xl md:text-8xl font-black text-slate-900 tracking-tighter uppercase leading-none font-mono">
                    {currentWord.word}
                </h2>
                <div className="inline-flex items-center gap-3 bg-violet-50 px-6 py-2 md:px-10 md:py-4 rounded-xl md:rounded-[2rem] border-2 border-violet-100 shadow-inner">
                    <span className="text-sm md:text-3xl">🔊</span>
                    <p className="text-xs md:text-xl font-black text-violet-700 italic">"{currentWord.hint || 'Suskaidyk šį žodį!'}"</p>
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="grid grid-cols-2 md:grid-cols-1 gap-2 md:gap-4 p-4 md:p-6">
            {[...gameData.categories]
                .sort((a, b) => a.syllable_count - b.syllable_count)
                .map((category, index) => {
                    const count = category.syllable_count;
                    const variants = [
                        'from-indigo-500 to-indigo-700 border-indigo-900',
                        'from-violet-500 to-violet-700 border-violet-900',
                        'from-purple-500 to-purple-700 border-purple-900',
                        'from-fuchsia-500 to-fuchsia-700 border-fuchsia-900'
                    ];

                    return (
                        <button
                            key={count}
                            onClick={() => handleCategoryClick(count)}
                            disabled={!!feedback}
                            className={`
                                premium-button-3d group bg-gradient-to-br ${variants[index % variants.length]} text-white p-4 md:p-10 rounded-2xl md:rounded-[3rem] 
                                transition-all transform active:scale-95 disabled:opacity-20 border-b-8
                            `}
                        >
                            <div className="text-4xl md:text-7xl font-black mb-1 md:mb-2 drop-shadow-2xl group-hover:scale-110 transition-transform font-mono">{count}</div>
                            <div className="text-[10px] md:text-xs font-black opacity-60 uppercase tracking-[0.2em] font-mono">{category.label}</div>
                        </button>
                    );
                })}
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-3xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-16 rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[20px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 md:w-32 md:h-32 bg-white rounded-full flex items-center justify-center text-5xl md:text-7xl shadow-2xl border-8 border-inherit animate-bounce">
                            {feedback === 'correct' ? '💎' : '⚠️'}
                        </div>
                        
                        <div className="text-center space-y-4 md:space-y-8">
                            <h3 className={`text-3xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'Analizė sėkminga!' : 'Struktūros klaida!'}
                            </h3>
                            
                            <div className={`p-4 md:p-10 rounded-2xl md:rounded-[3rem] border-4 ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100 text-emerald-900' : 'bg-rose-50 border-rose-100 text-rose-900'}`}>
                                <p className="text-sm md:text-2xl font-black leading-tight italic font-mono">
                                    {feedback === 'correct' ? 'Skiemenuotė nustatyta tobulai!' : 'Ištark žodį lėtai ir suskaičiuok skiemenis.'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SyllableSorterGame;
